// src/components/ServersParser/ServersParserTable.jsx
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const ServersParserTable = ({
  servers,
  onCopyIp,
  hasParsed = false,
  isProcessing = false
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  return (
    <div className="sp-servers-table-container">
      <div className="sp-servers-table-scroll">
        <table className="sp-servers-table">
        <thead>
          <tr>
            <th>{isEn ? 'Server (IP:PORT)' : 'Сервер (IP:PORT)'}</th>
            <th>{isEn ? 'Version' : 'Версия'}</th>
            <th>{isEn ? 'Online' : 'Онлайн'}</th>
            <th style={{ textAlign: 'right' }}>
              {isEn ? 'Vulnerability Level' : 'Уровень уязвимости'}
            </th>
          </tr>
        </thead>
        <tbody>
          {servers.length === 0 ? (
            <tr className="sp-table-empty-tr">
              <td colSpan="4" className="sp-table-empty-td">
                <div className="sp-table-empty-box">
                  {isProcessing ? (
                    <>
                      <div className="sp-empty-icon-wrap is-scanning">
                        <div className="sp-empty-scan-ring" />
                        <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" fill="none" strokeWidth="1.8">
                          <circle cx="12" cy="12" r="10" />
                          <circle cx="12" cy="12" r="6" />
                          <circle cx="12" cy="12" r="2" />
                          <line x1="12" y1="2" x2="12" y2="6" />
                          <line x1="12" y1="18" x2="12" y2="22" />
                        </svg>
                      </div>
                      <div className="sp-empty-title">
                        {isEn ? 'Scanning database for servers...' : 'Поиск серверов в базе мониторингов...'}
                      </div>
                      <div className="sp-empty-desc">
                        {isEn
                          ? 'Streaming active hosts matching your criteria via WebSocket.'
                          : 'Получение активных хостов по заданным критериям через WebSocket в реальном времени.'}
                      </div>
                    </>
                  ) : !hasParsed ? (
                    <>
                      <div className="sp-empty-icon-wrap">
                        <svg viewBox="0 0 24 24" width="26" height="26" stroke="currentColor" fill="none" strokeWidth="1.8">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                      </div>
                      <div className="sp-empty-title">
                        {isEn ? 'Table is empty' : 'Таблица пуста'}
                      </div>
                      <div className="sp-empty-desc">
                        {isEn
                          ? 'Select a vulnerability level or set filters and click "Start Parsing" to load servers.'
                          : 'Выберите уровень уязвимости или настройте фильтры и нажмите «Запустить парсинг» для загрузки серверов.'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="sp-empty-icon-wrap">
                        <svg viewBox="0 0 24 24" width="26" height="26" stroke="currentColor" fill="none" strokeWidth="1.8">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </div>
                      <div className="sp-empty-title">
                        {isEn ? 'No servers found' : 'Серверы не найдены'}
                      </div>
                      <div className="sp-empty-desc">
                        {isEn
                          ? 'No servers matched the specified criteria. Try adjusting your filters or online range.'
                          : 'По заданным критериям серверов не найдено. Попробуйте изменить фильтры или диапазон онлайна.'}
                      </div>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            servers.map((server) => {
              const percent = Math.min(
                100,
                Math.round((server.players / (server.maxPlayers || 1)) * 100)
              );
              const riskClass = `risk-${server.vulnLevel}`;
              const ipPort = `${server.ip}:${server.port}`;

              const badgeText = isEn
                ? (server.vulnLevel === 'high' ? 'Critical' : server.vulnLevel === 'medium' ? 'Medium' : 'Low')
                : server.vulnBadge;

              return (
                <tr key={server.id || ipPort}>
                  {/* IP:PORT + Кнопка копирования */}
                  <td>
                    <div className="sp-table-ip-cell">
                      <span className="sp-table-ip-text">{ipPort}</span>
                      <button
                        type="button"
                        className="sp-server-copy-icon-btn"
                        onClick={() => onCopyIp(ipPort)}
                        title={isEn ? 'Copy IP:PORT' : 'Скопировать IP:PORT'}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                    </div>
                  </td>

                  {/* Версия */}
                  <td>
                    <span className="sp-version-mono-badge">{server.version}</span>
                  </td>

                  {/* Онлайн */}
                  <td>
                    <div className="sp-online-box-wrap">
                      <div className="sp-online-num-line">
                        {server.players.toLocaleString()}{' '}
                        <span className="sp-online-max-dim">
                          / {server.maxPlayers.toLocaleString()}
                        </span>
                      </div>
                      <div className="sp-online-progress-track">
                        <div
                          className="sp-online-progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </td>

                  {/* Уровень уязвимости */}
                  <td style={{ textAlign: 'right' }}>
                    <span className={`sp-vuln-risk-pill ${riskClass}`}>
                      <span style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        backgroundColor: 'currentColor'
                      }} />
                      <span>{badgeText}</span>
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default ServersParserTable;
